import type { AnalysisType } from "@/components/ai/AnalysisTypeSelector";
import type { VocalAnalysisResult } from "@/components/ai/VocalAnalysisReport";
import { analyzeAudioFile, type AudioAnalysisResult } from "./audio-analysis";
import {
  analyzeWithEssentia,
  convertEssentiaToAudioAnalysis,
} from "./essentia-analysis";
import { generateAIComments, generateRecommendations } from "./ai-comments";
import {
  recognizeAndAnalyze,
  type HybridSpotifyDetails,
} from "./enhanced-music-recognition";
import { findSimilarArtists } from "./music-similarity";
import type { SpotifyAudioFeatures } from "./spotify-music-analysis";
import {
  analyzeMusicDNA,
  type AnalyzeMusicDNAInput,
  type MusicDNAResult,
} from "../music-dna/analyzeMusicDNA";
import { decodeAndValidateAudioFile } from "@/lib/utils/audio-usability";

/**
 * 분석 유형별로 다른 분석 파이프라인을 실행합니다.
 */
export async function analyzeByType(
  file: File,
  type: AnalysisType,
  options?: {
    useSpotify?: boolean;
  }
): Promise<VocalAnalysisResult> {
  console.log(`🎯 [${type}] 분석 파이프라인 시작:`, file.name);

  // 0. 업로드된 오디오의 기본 유효성 검사 (길이/볼륨)
  if (typeof window !== "undefined") {
    const validation = await decodeAndValidateAudioFile(file);
    if (!validation.ok) {
      console.error("❌ 기본 오디오 유효성 검사 실패:", validation.error);
      throw new Error(validation.error);
    }
  }

  // 1. Essentia.js 우선 시도 (정확도 향상)
  let audioData: AudioAnalysisResult;
  let spotifyDetails: HybridSpotifyDetails | undefined;

  if (options?.useSpotify) {
    try {
      console.log("🎧 Spotify 하이브리드 분석 시도...");
      const hybrid = await recognizeAndAnalyze(file);
      audioData = hybrid;
      if (hybrid.spotifyData) {
        spotifyDetails = hybrid.spotifyData;
      }
      console.log("✅ 하이브리드 분석 완료:", {
        tempo: audioData.tempo,
        energyLevel: audioData.energyLevel,
      });
    } catch (error) {
      // Spotify API 오류는 조용히 처리 (환경 변수 미설정 등)
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes("Spotify API") || errorMessage.includes("credentials")) {
        console.debug("ℹ️ Spotify API 미사용 (자격증명 없음), Essentia로 폴백");
      } else {
      console.warn("⚠️ Spotify 하이브리드 실패, Essentia로 폴백:", error);
      }
      audioData = await fallbackAnalyze(file);
    }
  } else {
    audioData = await fallbackAnalyze(file);
  }

  // 2. 유형별 점수 변환
  let scores: {
    pitchAccuracy: number;
    rhythmStability: number;
    energyDynamics: number;
    emotionalExpression: number;
    toneConsistency: number;
  };

  switch (type) {
    case "vocal":
      scores = convertToVocalScores(audioData);
      break;
    case "instrument":
      scores = convertToInstrumentScores(audioData);
      break;
    case "composition":
      scores = convertToCompositionScores(audioData);
      break;
  }

  const overallScore = Math.round(
    (scores.pitchAccuracy +
      scores.rhythmStability +
      scores.energyDynamics +
      scores.emotionalExpression +
      scores.toneConsistency) /
      5
  );

  console.log(`✅ [${type}] 점수 변환 완료:`, scores, "총점:", overallScore);

  // 3. AI 코멘트 생성
  const comments = await generateAIComments({
    ...scores,
    overallScore,
    tempo: audioData.tempo,
    duration: audioData.duration,
    analysisType: type,
  });

  console.log("✅ AI 코멘트 생성 완료");

  // 4. 연습 추천 생성
  const recommendations = await generateRecommendations({
    ...scores,
    overallScore,
    tempo: audioData.tempo,
    duration: audioData.duration,
    analysisType: type,
  });

  console.log("✅ 연습 추천 생성 완료");

  let analysisDetails: VocalAnalysisResult["analysisDetails"] | undefined;

  if (spotifyDetails) {
    const audioFeatures = (
      audioData as unknown as {
        audioFeatures?: SpotifyAudioFeatures;
      }
    ).audioFeatures;

    const similarArtists = findSimilarArtists(
      {
        trackPopularity: spotifyDetails.popularity,
        artistPopularity: spotifyDetails.artistPopularity,
        durationMs:
          spotifyDetails.durationMs ??
          (typeof audioData.duration === "number"
            ? audioData.duration * 1000
            : undefined),
        previewUrl: spotifyDetails.previewUrl,
        explicit: spotifyDetails.explicit,
        releaseDate: spotifyDetails.releaseDate,
        artistFollowers: spotifyDetails.artistFollowers,
      },
      {
        limit: 6,
        audioFeatures,
      }
    );

    analysisDetails = {
      spotify: {
        ...spotifyDetails,
        similarArtists,
      },
    };
  }

  const audioTempo = typeof audioData.tempo === "number" ? audioData.tempo : undefined;
  const dynamicRange = Math.min(Math.max(safeNumber(audioData.dynamicRange, 0.5), 0), 1);
  const spectralCentroidRaw =
    typeof audioData.spectralCentroid === "number"
      ? audioData.spectralCentroid
      : undefined;
  const spectralRatio = Math.min(Math.max(safeNumber(spectralCentroidRaw, 0.5), 0), 1);

  const genreCandidate =
    spotifyDetails?.genres?.[0] ??
    (typeof (audioData as { genre?: string }).genre === "string"
      ? (audioData as { genre?: string }).genre
      : undefined);

  const emotionCandidate =
    (audioData as { emotion?: string }).emotion ??
    (audioData as { mood?: string }).mood ??
    undefined;

  const energyLevel = Math.min(Math.max(safeNumber(audioData.energyLevel, 0.5), 0), 1);
  const dnaInput: AnalyzeMusicDNAInput = {
    tempo: audioTempo,
    energyLevel,
    dynamicRange,
    spectralCentroid: spectralRatio,
    emotion: emotionCandidate,
    genre: genreCandidate,
  };

  let musicDNA: MusicDNAResult | undefined;
  try {
    musicDNA = analyzeMusicDNA(dnaInput);
  } catch (dnaError) {
    console.warn("⚠️ Music DNA 분석에 실패했습니다:", dnaError);
  }

  return {
    ...scores,
    overallScore,
    comments,
    recommendations,
    ...(analysisDetails ? { analysisDetails } : {}),
    ...(musicDNA
        ? {
            musicDNA: {
              key: musicDNA.key,
              label: musicDNA.label,
              emoji: musicDNA.emoji,
              title: musicDNA.title,
              description: musicDNA.description,
              tagline: musicDNA.tagline,
              metrics: musicDNA.metrics,
            },
          }
        : {}),
  };
}

async function fallbackAnalyze(file: File): Promise<AudioAnalysisResult> {
  try {
    console.log("🎵 Essentia.js로 분석 시도...");
    const essentiaResult = await analyzeWithEssentia(file);
    const audioData = convertEssentiaToAudioAnalysis(essentiaResult);
    console.log("✅ Essentia.js 분석 완료 (고정밀):", audioData);
    return audioData;
  } catch (error) {
    console.warn("⚠️ Essentia.js 실패, Web Audio API로 폴백:", error);
    const audioData = await analyzeAudioFile(file);
    console.log("✅ Web Audio API 분석 완료:", audioData);
    return audioData;
  }
}

/**
 * 보컬 분석 점수 변환
 * - 음정 정확도 강조 (pitchAccuracy)
 * - 감정 표현 강조 (emotionalExpression)
 */
function convertToVocalScores(audioData: AudioAnalysisResult) {
  // 🎤 보컬 분석: 음정과 감정 표현이 가장 중요

  // 피치 정확도: 보컬에서 가장 중요 (120% 가중치)
  // pitchAccuracy는 0-1 범위이므로 100을 곱해서 점수화
  const pitchBase = safeNumber(audioData.pitchAccuracy, 0.7) * 100 * 1.2;
  const pitchAccuracy = Math.round(
    Math.max(55, Math.min(95, pitchBase))
  );

  // 감정 표현: 두 번째로 중요 (115% 가중치)
  const emotionBase =
    safeNumber(audioData.spectralCentroid, 0.45) * 55 +
    safeNumber(audioData.dynamicRange, 0.6) * 60;
  const emotionalExpression = Math.round(
    Math.max(58, Math.min(95, emotionBase))
  );

  // 리듬 안정성: 템포 유지 능력
  const rhythmBase = safeNumber(audioData.rhythmConsistency, 0.7) * 100 * 1.1;
  const rhythmStability = Math.round(
    Math.max(55, Math.min(95, rhythmBase))
  );

  // 에너지 다이내믹: 다이나믹 레인지
  const energyBase =
    safeNumber(audioData.dynamicRange, 0.6) * 85 +
    safeNumber(audioData.energyLevel, 0.65) * 15;
  const energyDynamics = Math.round(Math.max(55, Math.min(95, energyBase)));

  // 톤 일관성: 에너지 레벨의 안정성
  const toneBase =
    (1 - Math.abs(safeNumber(audioData.energyLevel, 0.65) - 0.5) * 1.5) * 100;
  const toneConsistency = Math.round(
    Math.max(
      55,
      Math.min(
        95,
        toneBase + safeNumber(audioData.rhythmConsistency, 0.7) * 5
      )
    )
  );

  return {
    pitchAccuracy,
    rhythmStability,
    energyDynamics,
    emotionalExpression,
    toneConsistency,
  };
}

/**
 * 연주 분석 점수 변환
 * - 리듬 안정성 강조 (rhythmStability)
 * - 톤 일관성 강조 (toneConsistency)
 */
function convertToInstrumentScores(audioData: AudioAnalysisResult) {
  // 🎸 연주 분석: 리듬과 터치가 가장 중요

  // 리듬 안정성: 가장 중요 (120% 가중치)
  const rhythmBase = safeNumber(audioData.rhythmConsistency, 0.75) * 100 * 1.2;
  const rhythmStability = Math.round(
    Math.max(55, Math.min(95, rhythmBase))
  );

  // 톤 일관성: 에너지 레벨 안정성 (110% 가중치)
  const toneBase =
    (1 - Math.abs((safeNumber(audioData.energyLevel, 0.55) - 0.5) * 1.3)) * 110;
  const toneConsistency = Math.round(
    Math.max(60, Math.min(95, toneBase))
  );

  // 에너지 다이내믹: 다이나믹 레인지 중시
  const energyBase =
    safeNumber(audioData.dynamicRange, 0.7) * 90 +
    safeNumber(audioData.energyLevel, 0.55) * 10;
  const energyDynamics = Math.round(Math.max(55, Math.min(95, energyBase)));

  // 피치 정확도: 화성적 요소 (보컬보다 약간 낮은 가중치)
  const pitchBase =
    safeNumber(audioData.pitchAccuracy, 0.68) * 100 * 1.05 +
    safeNumber(audioData.harmonicComplexity, 0.65) * 10;
  const pitchAccuracy = Math.round(
    Math.max(55, Math.min(92, pitchBase))
  );

  // 감정 표현: 음색과 뉘앙스 반영
  const emotionalExpression = Math.round(
    Math.max(
      58,
      Math.min(
        92,
        safeNumber(audioData.dynamicRange, 0.7) * 60 +
          safeNumber(audioData.spectralCentroid, 0.45) * 40
      )
    )
  );

  return {
    pitchAccuracy,
    rhythmStability,
    energyDynamics,
    emotionalExpression,
    toneConsistency,
  };
}

/**
 * 작곡 분석 점수 변환
 * - 구조 복잡도 강조 (energyDynamics)
 * - 감정 흐름 강조 (emotionalExpression)
 */
function convertToCompositionScores(audioData: AudioAnalysisResult) {
  // 🎼 작곡 분석: 구조와 창의성 강조

  // 구조적 완성도: 하모닉 복잡도와 구조 안정성 기반
  const pitchAccuracy = Math.round(
    Math.max(
      60,
      Math.min(
        95,
        safeNumber(audioData.harmonicComplexity, 0.68) * 65 + 30
      )
    )
  );

  // 멜로디 창의성: 스펙트럴 다양성과 피치 변동성
  const toneConsistency = Math.round(
    Math.max(
      55,
      Math.min(95, safeNumber(audioData.spectralCentroid, 0.45) * 70 + 20)
    )
  );

  // 감정 전달력: 다이내믹과 에너지 밸런스
  const emotionalExpression = Math.round(
    Math.max(
      58,
      Math.min(
        93,
        safeNumber(audioData.dynamicRange, 0.65) * 65 +
          safeNumber(audioData.energyLevel, 0.6) * 25
      )
    )
  );

  // 리듬 안정성: 전체 리듬 구조의 안정성
  const rhythmStability = Math.round(
    Math.max(
      55,
      Math.min(
        90,
        safeNumber(audioData.rhythmConsistency, 0.72) * 85 + 10
      )
    )
  );

  // 사운드 밸런스: 에너지 레벨과 다이내믹 조화
  const soundBalance = Math.round(
    Math.max(
      55,
      Math.min(92, safeNumber(audioData.energyLevel, 0.6) * 70 + 25)
    )
  );

  return {
    pitchAccuracy,
    rhythmStability,
    energyDynamics: soundBalance,
    emotionalExpression,
    toneConsistency,
  };
}

const safeNumber = (value: number | undefined | null, fallback: number) =>
  Number.isFinite(value) ? (value as number) : fallback;
